import React, { useContext, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { ThemeContext } from "../../hooks/ThemeContext";
import { UserContext } from "../../hooks/UserContext";
import { CartContext } from "../../hooks/CartContext";
import { Moon, Sun, Mail } from "lucide-react";
import FranceFlag from "../../assets/images/fr.png"; // Import de l'image PNG
import UKFlag from "../../assets/images/uk.png"; // Import de l'image PNG
import {
  FaShoppingCart,
  FaBars,
  FaTimes,
  FaChevronDown,
  FaUser,
  FaSignOutAlt,
} from "react-icons/fa";
import { useTranslation } from "react-i18next"; // Importation du hook i18n
import { api } from "../../services/baseUrl";

const Navbar = () => {
  const { user, isAuthenticated, role } = useContext(UserContext);
  const { cart } = useContext(CartContext);
  const { theme, toggleTheme, setLanguage } =
    useContext(ThemeContext);
  const { t, i18n } = useTranslation(); // Utilisation du hook pour la traduction
  const location = useLocation();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const toggleDrawer = () => setDrawerOpen(!drawerOpen);

  const toggleUserMenu = () => {
    setUserMenuOpen(!userMenuOpen);
    setDropdownOpen(false);
  };

  const handleLanguageChange = (lang) => {
    localStorage.setItem("language", lang);
    i18n.changeLanguage(lang);
    setLanguage(lang);
  };

  const closeMenus = () => {
    setDropdownOpen(false);
    setUserMenuOpen(false);
  };

  const handleLinkClick = () => {
    closeMenus();
  };

  const logout = () => {
    localStorage.removeItem("access_token");
    window.location.reload();
  };

  // Fermer les dropdowns en cliquant en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container') && 
          !event.target.closest('.user-menu-container')) {
        closeMenus();
      }
    };

    if (dropdownOpen || userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [dropdownOpen, userMenuOpen]);

  // Charger le nombre de messages non lus
  useEffect(() => {
    if (isAuthenticated) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 30000); // Refresh toutes les 30s
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  const fetchUnreadCount = async () => {
    try {
      const response = await api.get('/conversations/unread-count');
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Erreur chargement messages:', error);
      setUnreadCount(0);
    }
  };

  return (
    <nav className={`${theme === "dark" ? "bg-[#0f1114] text-white border-b border-white/10" : "bg-slate-50 text-gray-800"} sticky top-0 z-50 shadow-sm`}>
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={toggleDrawer}
            className="lg:hidden text-2xl hover:opacity-80"
            aria-label="Ouvrir le menu"
          >
            {drawerOpen ? <FaTimes /> : <FaBars />}
          </button>

          <Link to="/" className="flex items-center gap-3">
            <span className="text-lg sm:text-xl font-extrabold tracking-tight whitespace-nowrap xl:text-lg 2xl:text-xl [@media(min-width:1100px)]:text-base max-w-[180px] md:max-w-[260px] truncate">
              KRAYNE <span className="text-rose-400">AGENCY</span>
            </span>
          </Link>
        </div>

        <ul className="hidden lg:flex items-center gap-8 text-sm font-medium ml-8 xl:text-sm 2xl:text-base [@media(min-width:1100px)]:text-xs">
          {[
            ["/creations", t("nav_creations")],
            ...(!isAuthenticated || role === 1 
              ? [["/devenir-createur", "Devenir Créateur"]]
              : []
            ),
            ["/services", "Nos Services"],
            ["/shop", "Boutique"],
            ["/contact", t("nav_contact")],
          ].map(([path, label]) => {
            const active = location.pathname === path || location.pathname.startsWith(path + "/");
            return (
              <li key={path}>
                <Link
                  to={path}
                  onClick={handleLinkClick}
                  className={`px-3 py-2 rounded-full transition-all duration-150 flex items-center gap-2 ${
                    active
                      ? (theme === "dark" ? "bg-white/10 text-rose-300 border border-white/10" : "bg-rose-50 text-rose-600 shadow-inner")
                      : (theme === "dark" ? "text-gray-200 hover:bg-white/5 hover:text-rose-300" : "text-gray-700 hover:bg-rose-50 hover:text-rose-600")
                  }`}
                >
                  <span className="text-sm">{label}</span>
                </Link>
              </li>
            );
          })}
        </ul>
        {/* Actions desktop (style simple et propre) */}
        <div className="flex items-center gap-3 relative">
          {!isAuthenticated && (
            <Link
              to="/login"
              className="hidden lg:inline-block px-4 py-2 rounded-full bg-rose-500 text-white font-semibold hover:bg-rose-600 transition-colors text-sm"
            >
              Se connecter
            </Link>
          )}
          {isAuthenticated ? (
            <div className="relative user-menu-container">
              <button
                onClick={toggleUserMenu}
                className={`flex items-center gap-2 px-3 py-2 rounded-full transition-all group    ${theme === 'dark' ? 'border-white/10 bg-white/[0.03] hover:bg-white/[0.05]' : 'hover:bg-gray-50'}`}
              >
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold shadow-sm group-hover:shadow-md transition-shadow ${theme === 'dark' ? 'bg-gradient-to-br from-rose-950/20 via-slate-900 to-slate-950 text-white' : 'bg-gradient-to-br from-rose-500 to-purple-600 text-white'}`}>
                  {(user?.nickname || user?.name || 'U')[0].toUpperCase()}
                </div>
                <div className="hidden md:block text-left">
                  <span className="block text-sm font-semibold">
                    {user?.nickname || user?.name}
                  </span>
                  <span className={`block text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    {role === 3 ? 'Admin' : role === 2 ? 'Créateur' : 'Client'}
                  </span>
                </div>
                <FaChevronDown className={`text-xs transition-transform ${userMenuOpen ? 'rotate-180' : ''} ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
              </button>

              {userMenuOpen && (
                <div className={`absolute right-0 top-full mt-2 w-64 rounded-[28px] shadow-2xl border ${theme === 'dark' ? 'bg-[#161a20] border-white/10' : 'bg-white border-gray-200'} z-50 overflow-hidden`} style={theme === 'dark' ? { opacity: 1, backdropFilter: 'none', backgroundColor: '#161a20' } : undefined}>
                  {/* Header du menu */}
                  <div className={`px-4 py-3 border-b ${theme === 'dark' ? 'border-white/10 bg-[#161a20]' : 'border-gray-100 bg-gray-50'}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-base font-bold ${theme === 'dark' ? 'bg-gradient-to-br from-rose-950/20 via-slate-900 to-slate-950 text-white' : 'bg-gradient-to-br from-rose-500 to-purple-600 text-white'}`}>
                        {(user?.nickname || user?.name || 'U')[0].toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm truncate">{user?.nickname || user?.name}</p>
                        <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{user?.email}</p>
                      </div>
                    </div>
                  </div>

                  {/* Menu items */}
                  <div className={`py-2 ${theme === 'dark' ? 'bg-[#161a20]' : ''}`}>
                    <Link
                      to="/espace-client"
                      onClick={closeMenus}
                      className={`flex items-center gap-3 px-4 py-2.5 transition-colors ${theme === 'dark' ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}
                    >
                      <FaUser className={`${theme === 'dark' ? 'text-rose-400' : 'text-rose-600'}`} />
                      <span className="text-sm font-medium">Mon profil</span>
                    </Link>

                    {role !== 3 && ( // Cacher Messages pour les admins
                      <Link
                        to="/espace-client?tab=messages"
                        onClick={closeMenus}
                        className={`flex items-center justify-between px-4 py-2.5 transition-colors ${theme === 'dark' ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}
                      >
                        <div className="flex items-center gap-3">
                          <Mail className={`w-4 h-4 ${theme === 'dark' ? 'text-rose-400' : 'text-rose-600'}`} />
                          <span className="text-sm font-medium">Messages</span>
                        </div>
                        {unreadCount > 0 && (
                          <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full bg-rose-500 text-white text-xs font-bold">
                            {unreadCount}
                          </span>
                        )}
                      </Link>
                    )}

                    {role === 3 && (
                      <>
                        <Link
                          to="/dashboard"
                          onClick={closeMenus}
                          className={`flex items-center gap-3 px-4 py-2.5 transition-colors ${theme === 'dark' ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}
                        >
                          <FaUser className={`${theme === 'dark' ? 'text-rose-400' : 'text-rose-600'}`} />
                          <span className="text-sm font-medium">Admin Dashboard</span>
                        </Link>
                        <Link
                          to="/monia"
                          onClick={closeMenus}
                          className={`flex items-center gap-3 px-4 py-2.5 transition-colors ${theme === 'dark' ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}
                        >
                          <span className="text-base">🤖</span>
                          <span className="text-sm font-medium">Monia (IA)</span>
                        </Link>
                      </>
                    )}

                    {role === 2 && (
                      <Link
                        to="/mes-produits"
                        onClick={closeMenus}
                        className={`flex items-center gap-3 px-4 py-2.5 transition-colors ${theme === 'dark' ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}
                      >
                        <span className="text-base">📦</span>
                        <span className="text-sm font-medium">Espace Créateur</span>
                      </Link>
                    )}

                    {role === 2 && (
                      <Link
                        to="/mes-statistiques"
                        onClick={closeMenus}
                        className={`flex items-center gap-3 px-4 py-2.5 transition-colors ${theme === 'dark' ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}
                      >
                        <span className="text-base">📊</span>
                        <span className="text-sm font-medium">Mes Statistiques</span>
                      </Link>
                    )}
                  </div>

                  {/* Footer avec déconnexion */}
                  <div className={`border-t ${theme === 'dark' ? 'border-white/10' : 'border-gray-100'} pt-2 pb-2`}>
                    <button
                      onClick={() => {
                        logout();
                        closeMenus();
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-red-500 hover:bg-red-500/10 transition-colors"
                    >
                      <FaSignOutAlt />
                      <span className="text-sm font-semibold">Déconnexion</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : null}

          {/* Theme toggle */}
          <button
            onClick={() => toggleTheme()}
            aria-label="Toggle theme"
            className={` ${theme === 'dark' ? 'border-white/10 bg-white/[0.03] hover:bg-white/[0.05]' : 'border-gray-200 hover:bg-gray-50'} transition-colors`}
          >
            {theme === 'dark' ? <Sun className="text-yellow-300" /> : <Moon className="text-gray-700" />}
          </button>

          {/* Panier */}
          <Link to="/cart" className="relative hover:opacity-90 transition-opacity">
            <div className={`flex items-center gap-2 px-3 py-2  ${theme === 'dark' ? 'border-white/10 bg-white/[0.03] hover:bg-white/[0.05]' : 'border-gray-200 hover:bg-gray-50'} bg-transparent transition-colors`}>
              <FaShoppingCart className="text-lg" />
              <span className="hidden sm:block text-sm">{t('cart')}</span>
              {cart.items_count > 0 && (
                <span className="ml-2 inline-flex items-center justify-center text-xs font-semibold px-2 py-1 rounded-full bg-rose-500 text-white min-w-[1.25rem] h-5">
                  {cart.items_count}
                </span>
              )}
            </div>
          </Link>

        </div>
      </div>
      {/* Mobile drawer menu (animated) */}
        <div className="lg:hidden fixed inset-0 z-40 pointer-events-none">
        <div
          className={`absolute inset-0 transition-opacity duration-300 ${drawerOpen ? "opacity-100 pointer-events-auto" : "opacity-0"} ${theme === 'dark' ? 'bg-black/50' : 'bg-black/30'}`}
          onClick={toggleDrawer}
          aria-hidden={!drawerOpen}
        />

        <aside
          id="mobile-menu"
          role="dialog"
          aria-modal={drawerOpen}
          className={`absolute left-0 top-0 w-screen h-full p-8 sm:p-10 transform transition-transform duration-300 ${theme === 'dark' ? 'bg-[#0f1114] text-white' : 'bg-slate-50 text-gray-800'} ${
            drawerOpen ? "translate-x-0 pointer-events-auto" : "-translate-x-full pointer-events-none"
          } shadow-2xl`}
        >
          <div className="flex flex-col gap-4 mb-10">
            <div className="flex items-center gap-3 justify-between">
              <Link to="/" className="flex items-center gap-3" onClick={toggleDrawer}>
                <span className="text-lg sm:text-xl font-extrabold tracking-tight whitespace-nowrap">
                  KRAYNE <span className="text-rose-400">AGENCY</span>
                </span>
              </Link>
              <button onClick={toggleDrawer} className="p-3 rounded-full hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-rose-500" aria-label="Fermer le menu">
                <FaTimes className="w-6 h-6" />
              </button>
            </div>
            {/* Bouton Se connecter juste en dessous du titre */}
            {!isAuthenticated && (
              <Link
                to="/login"
                onClick={toggleDrawer}
                className="px-4 py-2 rounded-full bg-rose-500 text-white font-semibold hover:bg-rose-600 transition-colors text-sm w-full text-center mb-4"
              >
                Se connecter
              </Link>
            )}
          </div>

          <nav className="flex flex-col gap-5" aria-label="Mobile navigation">
            {/* Liens principaux, identiques à la version desktop */}
            <Link to="/creations" onClick={() => {navigate("/creations");toggleDrawer();}} className={`py-2 px-3 rounded ${location.pathname === "/creations" ? "bg-white/10" : "hover:bg-white/5"}`}>{t("nav_creations")}</Link>
            {(!isAuthenticated || role === 1) && (
              <Link to="/devenir-createur" onClick={() => {navigate("/devenir-createur");toggleDrawer();}} className={`py-2 px-3 rounded ${location.pathname === "/devenir-createur" ? "bg-white/10" : "hover:bg-white/5"}`}>Devenir Créateur</Link>
            )}
            <Link to="/services" onClick={() => {navigate("/services");toggleDrawer();}} className={`py-2 px-3 rounded ${location.pathname === "/services" ? "bg-white/10" : "hover:bg-white/5"}`}>Nos Services</Link>
            <Link to="/shop" onClick={() => {navigate("/shop");toggleDrawer();}} className={`py-2 px-3 rounded ${location.pathname === "/shop" ? "bg-white/10" : "hover:bg-white/5"}`}>Boutique</Link>
            <Link to="/contact" onClick={() => {navigate("/contact");toggleDrawer();}} className={`py-2 px-3 rounded ${location.pathname === "/contact" ? "bg-white/10" : "hover:bg-white/5"}`}>{t("nav_contact")}</Link>
            <Link to="/espace-client" onClick={() => {navigate("/espace-client");toggleDrawer();}} className={`py-2 px-3 rounded ${location.pathname === "/espace-client" ? "bg-white/10" : "hover:bg-white/5"}`}>Espace Client</Link>
            {role === 3 && (
              <Link to="/dashboard" onClick={() => {navigate("/dashboard");toggleDrawer();}} className={`py-2 px-3 rounded ${location.pathname === "/dashboard" ? "bg-rose-500/20 text-rose-400 font-semibold" : "hover:bg-white/5"}`}>Admin Dashboard</Link>
            )}
            {(role === 2 || role === 3) && (
              <Link to="/mes-produits" onClick={() => {navigate("/mes-produits");toggleDrawer();}} className={`py-2 px-3 rounded ${location.pathname === "/mes-produits" ? "bg-white/10" : "hover:bg-white/5"}`}>Espace Créateur</Link>
            )}
            {role === 2 && (
              <Link to="/mes-statistiques" onClick={() => {navigate("/mes-statistiques");toggleDrawer();}} className={`py-2 px-3 rounded ${location.pathname === "/mes-statistiques" ? "bg-white/10" : "hover:bg-white/5"}`}>Mes Statistiques</Link>
            )}
          </nav>
          {/* Bouton Se connecter déplacé en haut du menu hamburger */}

          {/* Sélecteur de langue supprimé pour épurer le menu mobile */}
        </aside>
      </div>
    </nav>
  );
};


export default Navbar;